'use client';

import React, { useEffect, useRef } from 'react';
import type { ThroughputPoint, LatencyPoint, RegionHealth } from '@/hooks/useDashboardRealtime';
import { useAnomalyDetection } from '@/hooks/useAnomalyDetection';
import type { AnomalyFlag, SLAPrediction } from '@/hooks/useAnomalyDetection';
import toast from 'react-hot-toast';

interface Props {
  throughput: ThroughputPoint[];
  latency: LatencyPoint[];
  regions: RegionHealth[];
  loading: boolean;
}

const severityConfig = {
  critical: {
    bg: 'bg-danger/10',
    border: 'border-danger/30',
    badge: 'bg-danger/20 text-danger',
    dot: 'bg-danger',
    icon: '⚠',
  },
  warning: {
    bg: 'bg-warning/10',
    border: 'border-warning/30',
    badge: 'bg-warning/20 text-warning',
    dot: 'bg-warning',
    icon: '◈',
  },
};

const riskConfig = {
  high: { color: 'text-danger', bar: 'bg-danger', badge: 'bg-danger/15 text-danger' },
  medium: { color: 'text-warning', bar: 'bg-warning', badge: 'bg-warning/15 text-warning' },
  low: { color: 'text-success', bar: 'bg-success', badge: 'bg-success/15 text-success' },
};

function AnomalyCard({ anomaly }: { anomaly: AnomalyFlag }) {
  const cfg = severityConfig[anomaly.severity];
  return (
    <div className={`rounded-lg border p-3 ${cfg.bg} ${cfg.border}`}>
      <div className="flex items-start gap-2.5">
        <span className={`mt-0.5 text-sm ${anomaly.severity === 'critical' ? 'text-danger' : 'text-warning'}`}>
          {cfg.icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-foreground">{anomaly.title}</span>
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${cfg.badge}`}>
              {anomaly.type.toUpperCase()}
            </span>
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${cfg.badge}`}>
              {anomaly.severity.toUpperCase()}
            </span>
          </div>
          <p className="text-[11px] text-muted mt-1 leading-relaxed">{anomaly.description}</p>
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="text-[10px] text-foreground-muted font-mono bg-surface-raised px-1.5 py-0.5 rounded">
              {anomaly.metric}
            </span>
          </div>
          <p className="text-[11px] text-primary mt-1.5 font-medium">
            → {anomaly.recommendation}
          </p>
        </div>
      </div>
    </div>
  );
}

function SLACard({ prediction }: { prediction: SLAPrediction }) {
  const cfg = riskConfig[prediction.riskLevel];
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-foreground truncate">{prediction.region}</span>
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${cfg.badge}`}>
            {prediction.riskLevel.toUpperCase()}
          </span>
        </div>
        <p className="text-[11px] text-muted mt-0.5">{prediction.affectedMetric}</p>
      </div>
      <div className="text-right shrink-0">
        <p className={`text-xs font-semibold font-mono ${cfg.color}`}>{prediction.estimatedBreachIn}</p>
        <div className="flex items-center gap-1 mt-1 justify-end">
          <div className="w-16 h-1 bg-surface-raised rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${cfg.bar}`}
              style={{ width: `${prediction.confidence}%` }}
            />
          </div>
          <span className="text-[10px] text-muted font-mono">{prediction.confidence}%</span>
        </div>
      </div>
    </div>
  );
}

export default function AnomalyDetectionPanel({ throughput, latency, regions, loading }: Props) {
  const { analysis, isAnalyzing, error, runAnalysis } = useAnomalyDetection();
  const hasRunRef = useRef(false);

  // Auto-run once data is loaded
  useEffect(() => {
    if (!loading && throughput.length > 0 && latency.length > 0 && !hasRunRef.current) {
      hasRunRef.current = true;
      runAnalysis(throughput, latency, regions);
    }
  }, [loading, throughput, latency, regions, runAnalysis]);

  useEffect(() => {
    if (error) toast.error(`AI Analysis: ${error}`);
  }, [error]);

  const handleRefresh = () => {
    runAnalysis(throughput, latency, regions);
  };

  const criticalCount = analysis?.anomalies.filter(a => a.severity === 'critical').length ?? 0;
  const highRiskSLA = analysis?.slaPredictions.filter(p => p.riskLevel === 'high').length ?? 0;

  return (
    <div className="bg-surface border border-border rounded-xl p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center">
            <span className="text-primary text-sm">✦</span>
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">AI Anomaly Detection</p>
            <p className="text-[11px] text-muted mt-0.5">
              OpenAI · Throughput &amp; Latency · SLA Risk
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {analysis && (
            <span className="text-[10px] text-muted font-mono">
              {analysis.analyzedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={isAnalyzing || loading}
            className="text-[11px] bg-primary/10 text-primary border border-primary/25 rounded-lg px-2.5 py-1 hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isAnalyzing ? (
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 border border-primary/60 border-t-primary rounded-full animate-spin inline-block" />
                Analyzing…
              </span>
            ) : (
              'Re-analyze'
            )}
          </button>
        </div>
      </div>

      {/* Loading skeleton */}
      {(loading || isAnalyzing) && !analysis && (
        <div className="space-y-3">
          <div className="h-4 bg-surface-raised rounded animate-pulse w-3/4" />
          <div className="h-16 bg-surface-raised rounded animate-pulse" />
          <div className="h-16 bg-surface-raised rounded animate-pulse" />
          <div className="h-12 bg-surface-raised rounded animate-pulse w-1/2" />
        </div>
      )}

      {/* Analysis results */}
      {analysis && (
        <div className="space-y-4">
          {/* Summary bar */}
          <div className="flex items-center gap-3 p-2.5 bg-surface-raised rounded-lg border border-border">
            <span className="text-primary text-sm">✦</span>
            <p className="text-[11px] text-foreground-muted leading-relaxed flex-1">{analysis.summary}</p>
            <div className="flex items-center gap-2 shrink-0">
              {criticalCount > 0 && (
                <span className="text-[10px] font-mono bg-danger/15 text-danger px-1.5 py-0.5 rounded">
                  {criticalCount} critical
                </span>
              )}
              {highRiskSLA > 0 && (
                <span className="text-[10px] font-mono bg-warning/15 text-warning px-1.5 py-0.5 rounded">
                  {highRiskSLA} SLA at risk
                </span>
              )}
              {criticalCount === 0 && highRiskSLA === 0 && (
                <span className="text-[10px] font-mono bg-success/15 text-success px-1.5 py-0.5 rounded">
                  All clear
                </span>
              )}
            </div>
          </div>

          {/* Anomaly flags */}
          {analysis.anomalies.length > 0 ? (
            <div>
              <p className="text-[11px] font-semibold text-foreground-muted uppercase tracking-wider mb-2">
                Flagged Anomalies ({analysis.anomalies.length})
              </p>
              <div className="space-y-2">
                {analysis.anomalies.map(anomaly => (
                  <AnomalyCard key={anomaly.id} anomaly={anomaly} />
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 bg-success/5 border border-success/20 rounded-lg">
              <span className="text-success text-sm">✓</span>
              <p className="text-[11px] text-success">No anomalies detected in throughput or latency patterns.</p>
            </div>
          )}

          {/* SLA predictions */}
          {analysis.slaPredictions.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-foreground-muted uppercase tracking-wider mb-2">
                SLA Breach Predictions
              </p>
              <div className="bg-surface-raised rounded-lg border border-border px-3 divide-y divide-border">
                {analysis.slaPredictions.map((pred, i) => (
                  <SLACard key={`${pred.region}-${i}`} prediction={pred} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state before first analysis */}
      {!analysis && !isAnalyzing && !loading && (
        <div className="text-center py-6">
          <p className="text-sm text-muted">Click Re-analyze to run AI anomaly detection</p>
        </div>
      )}
    </div>
  );
}
